import { styled, css } from 'styled-components';

import { Avatar, Text, focusStyle } from '@ssnc/bsds-core';

import { getTenantDisplayName, type TenantWithLoginUrl } from '../../lib/tenants';

interface TenantTileProps {
  tenant: TenantWithLoginUrl;
}

/**
 * A card rather than bsds-core's Button: Button is a fully-rounded pill with
 * centred, wrapping text, which turns into uneven blue ovals once it's stretched
 * into a grid tile. Styled from theme tokens only so it still tracks the design
 * system (including dark mode).
 */
const Card = styled.a(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.space[200]};
    min-width: 0;
    padding: ${theme.space[300]};
    border: ${theme.stroke.structure} ${theme.sys.color.border.default};
    border-radius: ${theme.borderRadius[150]};
    text-decoration: none;
    cursor: pointer;
    transition:
      border-color 120ms ease,
      box-shadow 120ms ease,
      transform 120ms ease;

    /* Long names truncate; the avatar should never be the thing that shrinks. */
    & > :first-child {
      flex-shrink: 0;
    }

    &:hover {
      border-color: ${theme.sys.color.border.focus.default};
      background-color: ${theme.sys.color.background.container.neutral.subtle.default};
      box-shadow: ${theme.elevations[200]};
      transform: translateY(-1px);
    }

    &:focus-visible {
      ${focusStyle({ theme })};
    }
  `,
);

// Lets the truncated Text actually ellipsize instead of overflowing the card.
const Name = styled.span`
  display: grid;
  min-width: 0;
`;

export function TenantTile({ tenant }: TenantTileProps) {
  const displayName = getTenantDisplayName(tenant.name);

  return (
    <Card href={tenant.loginUrl} role="listitem" aria-label={`Go to ${tenant.name} login`} title={tenant.name}>
      <Avatar name={displayName} image={tenant.logoUrl ?? undefined} size="medium" />
      <Name>
        <Text type="body" styleAs="uiEmphasised" truncated>
          {displayName}
        </Text>
      </Name>
    </Card>
  );
}
